import { defineStore } from 'pinia'
import { usePodStore } from './PodStore.js'
import { useAuthStore } from './AuthStore.js';
import { useCommonStore } from './CommonStore.js';
import { useAccountStore } from './AccountStore.js'
import { useRecycleBinStore } from './RecycleBinStore.js';
import Helper from '@/components/helper';
import Api from '../api/Api'

export const useNodeStore = defineStore('node', {
  state: () => ({
    allNodes: [],

    accessPointIds: [],
    currentNodeId: null,
    isLoadingAccessPoints: false,

    selectedNodeIds: [],
    iDeletedThoseNodeIds: [],

    nodeview: window.localStorage.nodeview || 'grid',
    viewNodeMoved: false,
    viewNodeDeleted: false,
    viewNodeDeletedId: null,
    deletedNodes: [],
    deletedPods: [],
  }),
  getters: {
    getNodeById(state) {
      return function(nodeId) {
        return state.allNodes.find(x => x.id === nodeId);
      }
    },
    getChildrenById(state) {
      return function(parentId) {
        return state.allNodes
          .filter(x => x.parentid === parentId)
          .sort(function(a, b) {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          });
      }
    },
    getNodesByType(state) {
      return function(typeId) {
        const commonStore = useCommonStore();
        const getNodeTypes = commonStore.nodeTypes;

        const authStore = useAuthStore();
        const getRootNodeId = authStore.rootNodeId;

        return state.allNodes
        .filter(x => x.type === typeId)
        .filter(function(x) {
          // For projects and clients we need to match parent nodes
          // Note: we are assuming that clients are located right inside the tenant folder
          // and the projects are located right inside the tenant or client folders
          if (getRootNodeId && (typeId === getNodeTypes.PROJECT || typeId === getNodeTypes.CLIENT)) {
            if (x.parentid && x.parentid === getRootNodeId) {
              return true;
            }

            if (x.parentid) {
              const parent = state.allNodes.find(x => x.id === x.parentid);
              if (parent && parent.parentid === getRootNodeId) {
                return true;
              }
            }

            return false;
          }

          return true;
        })
        .sort(function(a, b) {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });
      }
    },
    getAccessPoints: function(state) {
      return state.allNodes.filter(x => state.accessPointIds.indexOf(x.id) >= 0);
    },
    getIsLoadingAccessPoints: function(state) {
      return state.isLoadingAccessPoints;
    },
    getCurrentNode: function(state) {
        return state.allNodes.find(x => x.id === state.currentNodeId);
    },
    getChildNodes: function(state) {
      if (!state.currentNodeId) {
        return [];
      }

      return state.allNodes
        .filter(x => x.parentid === state.currentNodeId)
        .sort(function(a, b) {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });
    },
    getArchivedNodes: function(state) {

      return function () {
        return state.allNodes
          .filter(x => x.archived)
          .filter(function(x) {
            if (!x.parentid) {
              return false
            }

            // Check if parent is archived. If so, skip this node.
            const parent = state.allNodes.find(node => node.id === x.parentid);
            if (parent && parent.archived) {
              return false;
            }

            return true;
          });
      }
    },
    getSelectedNodes: function(state) {
      return state.allNodes.filter(x => state.selectedNodeIds.indexOf(x.id) >= 0);
    },
    getSelectedNodeIds: function (state) {
      return state.selectedNodeIds;
    },
    getNodeView: function(state) {
      return state.nodeview;
    },
    isViewNodeMoved: function(state) {
      return state.viewNodeMoved;
    },
    isViewNodeDeleted: function(state) {
      return state.viewNodeDeleted;
    },
    getCurrentNodeId: function (state) {
      return state.currentNodeId;
    },
    hasSelectedNodes: function(state) {
      return state.selectedNodeIds.length > 0;
    },

    isActiveNode: function(state) {
      return (nodeId) => state.allNodes.some((x) => x.id === nodeId);
    },
    findActiveParentNodeIdOfDeletedNode: function(state) {
      return function(nodeId) {
        const deletedNode = state.deletedNodes.find((x) => x.id === nodeId);

        if (!deletedNode) {
          return null; // Node not found in deletedNodes
        }

        // Start with the immediate parent of the deleted node
        let currentParent = deletedNode.parent;

        // Traverse up the parent chain
        while (currentParent) {
          // Check if this parent exists in allNodes (meaning it's still active)
          const activeNode = state.allNodes.find((x) => x.id === currentParent.id);

          if (activeNode) {
            return currentParent.id; // Found an active parent
          }

          // Move up to the next parent in the chain
          currentParent = currentParent.parent;
        }

        // No active parents found, could return a default like root node or null
        return null;
      }
    },
    getViewNodeDeletedId: function(state) {
      return state.viewNodeDeletedId;
    }
  },
  actions: {
    _addOrUpdateNodes(nodes) {
      if (!Array.isArray(nodes)) {
        nodes = [nodes];
      }

      // Create a map of existing nodes for faster lookups
      const existingNodesMap = new Map(
        this.allNodes.map((node) => [ node.id, node ]),
      );

      // Collect all nodes to be updated or added
      const optionalNodesToProcess = [];

      // Add children and parents to the processing list
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        // Add children if they exist
        if (node.children && Array.isArray(node.children)) {
          for (const child of node.children) {
            // Ensure child has an ID
            if (child.id) {
              existingNodesMap.set(child.id, child);
            }
          }
        }

        // Add parent if it exists
        if (node.parent) {
          optionalNodesToProcess.push(node.parent);

          // Add grandparent if it exists
          if (node.parent.parent) {
            optionalNodesToProcess.push(node.parent.parent);
          }
        }

        // Add the new or updated node to the processing list
        existingNodesMap.set(node.id, node);
      }

      // Process optional nodes (children and parents)
      for (const optionalNode of optionalNodesToProcess) {
        // Add the optional node if it doesn't exist in the map
        if (!existingNodesMap.has(optionalNode.id)) {
          existingNodesMap.set(optionalNode.id, optionalNode);
        } else {
          // If it exists, replace it with the new one if it has a different parent ID or root node
          const existingNode = existingNodesMap.get(optionalNode.id);

          // Replace the existing node with the new one if it has different parent
          if (optionalNode.parentid && existingNode.parentid) {
            if (optionalNode.parentid !== existingNode.parentid) {
              existingNodesMap.set(optionalNode.id, optionalNode);
            }

          // Replace the existing node with the new one if it has different root node
          } else if (optionalNode.rootpath && existingNode.rootpath) {
            if (optionalNode.rootpath !== existingNode.rootpath) {
              existingNodesMap.set(optionalNode.id, optionalNode);
            }
          }
        }
      }

      // Set all nodes at once with a single state mutation
      this.allNodes = [ ...existingNodesMap.values() ];
    },
    _setAccessPoints: function(nodes) {
      this.accessPointIds = nodes.map(x => x.id);
    },
    _setCurrentNode: function(node) {
      if (node) {
        this.currentNodeId = node.id;
      } else {
        this.currentNodeId = null;
      }

      this.selectedNodeIds = [];
      this.viewNodeDeleted = false;
      this.viewNodeMoved = false;
    },
    _setViewNodeDeleted: function(nodeID) {
      if (Array.isArray(nodeID)) {
        for (let i = 0; i < nodeID.length; i++) {
          if (this.iDeletedThoseNodeIds.indexOf(nodeID[i]) >= 0) {
            return;
          }
        }
      } else {
        // No need to address this change beacuse i deleted this node
        if (this.iDeletedThoseNodeIds.indexOf(nodeID) >= 0) {
          return;
        }
      }

      if (this.currentNodeId) {
        const currentNode = this.allNodes.find((x) => x.id === this.currentNodeId);

        // Split the rootpath to get the ids and remove empty strings.
        const ids = currentNode.rootpath.split('/').filter(Boolean);

        if (Array.isArray(nodeID)) {
          for (let i = 0; i < nodeID.length; i++) {
            if (ids.includes(nodeID[i])) {
              this.viewNodeDeleted = true;
              this.viewNodeDeletedId = nodeID[i];
              break;
            }
          }
        } else {
          if (ids.indexOf(nodeID.toString()) >= 0) {
            this.viewNodeDeleted = true;
            this.viewNodeDeletedId = nodeID;
          }
        }
      }
    },
    _setViewNodeMoved: function(nodeID) {
      if (this.currentNodeId) {
        const currentNode = this.allNodes.find(x => x.id == this.currentNodeId);

        const ids = currentNode.rootpath.split('/');
        if (ids.indexOf(nodeID.toString()) >= 0) {
          this.viewNodeMoved = true;
        }
      }
    },
    _deleteNodes: function(nodeIDs) {
      if (nodeIDs.indexOf(this.currentNodeId) >= 0) {
        this.currentNodeId = null;
      }

      const nodeIdsForDeletion = [...nodeIDs];

      // Remove the children of the nodes to be deleted
      for (let i = 0; i < this.allNodes.length; i++) {
        if (this.allNodes[i].parentid && nodeIdsForDeletion.indexOf(this.allNodes[i].parentid) >= 0) {
          nodeIdsForDeletion.push(this.allNodes[i].id);
        }
      }

      const clonedNodes = [];
      for (let i = this.allNodes.length - 1; i >= 0; i--) {
        if (nodeIdsForDeletion.indexOf(this.allNodes[i].id) >= 0) {
          continue;
        }

        clonedNodes.push(this.allNodes[i]);
      }

      this.allNodes = clonedNodes;
    },
    _addIDeletedThoseNodeIds: function(nodeIds) {
      for (let i = 0; i < nodeIds.length; i++) {
        if (this.iDeletedThoseNodeIds.indexOf(nodeIds[i]) < 0) {
          this.iDeletedThoseNodeIds.push(nodeIds[i]);
        }
      }
    },
    _removeIDeletedThoseNodeIds: function(nodeIds) {
      for (let i = 0; i < nodeIds.length; i++) {
        const idIndex = this.iDeletedThoseNodeIds.indexOf(nodeIds[i]);

        if (idIndex >= 0) {
          this.iDeletedThoseNodeIds.splice(idIndex, 1);
        }
      }
    },
    _deleteLogo: function(nodeID) {
      for (let item of this.allNodes) {
        if (item.id == nodeID && item.clientlogourl) {
          // delete item.clientlogourl;
          delete item['clientlogourl'];
          break;
        }
      }
    },
    _toggleSelection: function(node) {
      let index = this.selectedNodeIds.indexOf(node.id);

      if (index >= 0) {
        this.selectedNodeIds.splice(index, 1);
      } else {
        this.selectedNodeIds.push(node.id);
      }
    },
    _deselectAllNodes: function() {
      this.selectedNodeIds = [];
    },
    _toggleNodeView: function() {
      if (this.nodeview == 'grid') {
        this.nodeview = 'list';
      } else {
        this.nodeview = 'grid';
      }

      window.localStorage.nodeview = this.nodeview;
    },
    _setNodeView: function(nodeView) {
      this.nodeview = (nodeView === 'list') ? 'list' : 'grid';

      window.localStorage.nodeview = this.nodeview;
    },
    _updateNodeProperty: function(payload) {
      for (let i = 0; i < this.allNodes.length; i++) {
        if ((this.allNodes[i].id === payload.nodeId) && (payload.propertyName in this.allNodes[i])) {
          this.allNodes[i][payload.propertyName] = payload.newValue;
        }
      }
    },
    _selectSubfolders: function(payload) {
      const { nodeId } = payload;

      if (nodeId) {
        const selectedNodeIds = [];

        for (let i = 0; i < this.allNodes.length; i++) {
          if (this.allNodes[i].parentid === nodeId) {
            selectedNodeIds.push(this.allNodes[i].id);
          }
        }

        this.selectedNodeIds = selectedNodeIds;
      }
    },
    _setDeletedNodes: function(nodeIDs, deletedByMe = false) {
      for (const nodeID of nodeIDs) {
        const node = this.allNodes.find((x) => x.id === nodeID);

        if (node) {
          // Create a deep copy of the node to avoid reference issues
          const deletedNode = { id: node.id, deletedByMe };

          // Build the parent chain by traversing up the hierarchy
          let currentNode = deletedNode;
          let parentId = node.parentid;

          while (parentId) {
            const parentNode = this.allNodes.find((x) => x.id === parentId);
            if (!parentNode) break;

            // Add parent to the chain
            currentNode.parent = { id: parentNode.id };

            // Move up the chain
            currentNode = currentNode.parent;
            parentId = parentNode.parentid;
          }

          this.deletedNodes.push(deletedNode);
        }
      }
    },

    getAffectedFtpUsers: async function(nodeIds) {
      try {
        const authStore = useAuthStore();
        const response = await Api.ftpUser.getAffectedFtpUsers(authStore.rootNodeId, nodeIds);
        return response.data;
      } catch {
        return [];
      }
    },

    // used in the side node menu
    // get node info with child nodes
    // used in the move/copy nodes and pods dialogs
    getNodeInfo: async function(nodeID) {
      const response = await Api.node.getNodeByID(nodeID);
      this._addOrUpdateNodes(response.data);
      return response.data;
    },
    // getting child nodes and pods of specific node
    // used in the nodes listing view component
    getFullNodeInfo: async function(nodeID) {
      const response = await Api.node.getNodeByID(nodeID);
      this._addOrUpdateNodes(response.data);
      return response;
    },
    refreshAccessPoints: async function() {
      this.isLoadingAccessPoints = true;
      try {
          const result_1 = await Api.node.getAccessPoints();
          this._addOrUpdateNodes(result_1.data);
          this._setAccessPoints(result_1.data);
          return result_1.data;
      } finally {
          this.isLoadingAccessPoints = false;
      }
    },

    setActiveNode: async function(nodeID) {
      const result = await Api.node.getNodeByID(nodeID);

      const currentNode = result.data;
      this._addOrUpdateNodes(currentNode);
      this._setCurrentNode(currentNode);
      //the following block is used to determine if in the FCP extension and to attach the node id to it
      if (Helper.isFCPMessageHandler('setNode')) {
          const nodeObject = JSON.stringify({ action: "setNode", nodeID: nodeID });
          Helper.invokeFCPMessageHandler('setNode', nodeObject);
      }
      return result.data;
    },

    viewNode: async function(nodeID) {
      // console.log("View node!!!")

      // NOTE: this is used in the pod store - create new pod, addOrUpdatePod
      // Fixing issue #10601
      window.isNodeTransitioning = true;

      const podStore = usePodStore();

      // clear up the selected and viewing pods and assets
      podStore.resetPods();

      try {
        let result = await this.setActiveNode(nodeID);
        if (result && result.pods && result.pods.length > 0) {
          podStore.addOrUpdatePods(result.pods);
        }

        return result;
      } finally {
        window.isNodeTransitioning = false;
      }
    },

    viewRoots: function() {
      this._setCurrentNode(null);

      const podStore = usePodStore();
      return podStore.resetPods(null);
    },
    createNode: async function(params) {
      if (!params.note) {
        params.note = "";
      }

      const result = await Api.node.createNode(params);
      this._addOrUpdateNodes(result.data);
      return result.data;
    },
    updateNode: async function(params) {
      const nodeID = params.id;
      delete params.id;

      if (!params.note) {
        params.note = "";
      }

      if (params.retentionperiod === 0) {
        delete params.retentionperiod;
        delete params.retentionperiodtype;
      }

      const result = await Api.node.updateNode(params, nodeID);
      this._addOrUpdateNodes(result.data);
      return result.data;
    },
    updateFolder: async function(payload) {
      const nodeId = payload.id;
      const data = {
        name: payload.name,
        note: payload.note,
        teamonly: payload.teamonly,
      };

      if (payload.retentionperiod > 0 && payload.retentionperiodtype) {
        data.retentionperiod = payload.retentionperiod;
        data.retentionperiodtype = payload.retentionperiodtype;
      }

      const result = await Api.node.updateFolder(nodeId, data);
      this._addOrUpdateNodes(result.data);
      return result.data;
    },
    getUsersForNodes: async function(nodeIDs) {
      const result = await Api.node.getAttachedUsers(nodeIDs);
      return {
        "enabled": result.activeUsers,
        "disabled": result.disabledUsers,
      };
    },

    deleteNodes: async function(nodeIDs, permanently = false) {
      if (!permanently) {
        this._addIDeletedThoseNodeIds(nodeIDs);
        this._setDeletedNodes(nodeIDs, true);
      }

      try {
        if (nodeIDs.length === 1) {
          return await Api.node.deleteNode(nodeIDs[0], permanently);
        } else if (nodeIDs.length > 1) {
          return await Api.node.deleteManyNode(nodeIDs, permanently);
        }
      } finally {
        if (!permanently) {
          this._removeIDeletedThoseNodeIds(nodeIDs);
        }
      }

      // Do not commit state-change.
      // Instead wait for push notification to arrive.
      // context.commit("deleteNodes", nodeIDs);

      // Checkout US #12212 for the reason why.
      // We just keep a list of deleted node IDs by me,
      // so that we know when NOT to show the NodeDeletedDialog.
      // We should show the NodeDeletedDialog only when
      // somebody else deletes the node we are currently in.

    },

    deleteTenants: async function(tenantIDs) {
      let promises = tenantIDs.map(x => Api.tenant.deleteTenant(x));
      await Promise.all(promises);
      this._deleteNodes(tenantIDs);
    },
    deleteLogo: async function(nodeID) {
      const response = await Api.node.deleteLogo(nodeID);
      console.log("Node logo deleted", response);
      this._deleteLogo(nodeID);
    },

    toggleSelection: function(node) {
      this._toggleSelection(node);
    },
    deselectAllNodes: function() {
      this._deselectAllNodes();
    },
    toggleNodeView: function() {
      this._toggleNodeView();

      const accountStore = useAccountStore();
      accountStore.changeFolderView({ view: this.getNodeView });
    },
    getSubnodes: async function(payload) {
      const response = await Api.node.getSubnodes(payload.nodeid, payload.type);
      this._addOrUpdateNodes(response.data);
      return response.data;
    },
    moveManyNodes: async function(payload) {
      const response = await Api.node.moveManyNodes(payload);
      if (response?.data?.validation === false) {
        // console.log("Move many nodes response", response);
        this._addOrUpdateNodes(response.data.nodes);
      }

      return response.data;
    },
    copyManyNodes: function(payload) {
      return Api.node.copyManyNodes(payload);
    },
    copyManyNodesCrossTenant: async function(payload) {
      return Api.node.copyManyNodesCrossTenant(payload);
    },
    updateNote: function(payload) {
      return Api.node.updateNote(payload);
    },
    updateProjectStatus: async function(params) {
      const result = await Api.node.updateProjectStatus(params, params.id);
      this._addOrUpdateNodes(result.data);
      return result.data;
    },
    renameNode: async function (payload) {
      const result = await Api.node.renameNode(payload);

      // Update the node name in allNodes array
      for (let i = 0; i < this.allNodes.length; i++) {
        if (this.allNodes[i].id === payload.id) {
          this.allNodes[i].name = payload.name;
          break;
        }
      }

      return result.data;
    },
    getProperties: async function(nodeID) {
      const response = await Api.node.getProperties(nodeID);
        return response.data;
    },
    nodeCreatedNotification: function(node) {
      this._addOrUpdateNodes(node);
    },
    nodeUpdatedNotification: function(nodes) {
      const nodesToUpdate = Array.isArray(nodes) ? nodes : [nodes];

      this._addOrUpdateNodes(nodesToUpdate);
      this.updateOtherStoresNode(nodesToUpdate);
    },
    nodeDeletedNotification: function(nodes) {
      let nodeIdsSet = new Set();

      for (const node of nodes) {
        if (Array.isArray(node?.childIds)) {
          nodeIdsSet.add(node.id);
          for (const childId of node.childIds) {
            nodeIdsSet.add(childId);
          }
        } else {
          nodeIdsSet.add(node.id);
        }
      }

      const nodeIdsForDeletion = [...nodeIdsSet];

      const podStore = usePodStore();
      const isViewPodDeleted = podStore.isViewPodDeleted;
      if (!isViewPodDeleted) {
        this._setViewNodeDeleted(nodeIdsForDeletion);
      }

      this._deleteNodes(nodeIdsForDeletion);
    },
    nodeMovedNotification: function(nodes) {
      const movedNode = nodes.find((node) => node.id === this.currentNodeId);

      if (movedNode) {
        this._setViewNodeMoved(movedNode.id);
      }

      this._addOrUpdateNodes(nodes);
      this.updateOtherStoresNode(nodes);
    },
    nodeCopiedNotification: function(node) {
      this._addOrUpdateNodes(node);
    },
    updateOtherStoresNode(node) {
      const recycleBinStore = useRecycleBinStore();
      recycleBinStore.updateRecycleBinNode(node);
    },
    softDeleteTenant: async function(tenantId) {
      return Api.node.softDeleteTenant(tenantId);
    },
    updateTeamOnly: async function (payload) {
      const nodeId = payload.id;
      const data = {
        teamonly: payload.teamonly,
      };

      const result = await Api.node.updateTeamOnly(nodeId, data);
      this._addOrUpdateNodes(result);
      return result;
    },
    updateTeamOnlyNodes: async function(payload) {
      const updatedNodes = await Api.node.updateTeamOnlyNodes(payload);
      return updatedNodes;
    },
    decreasePodCountOfNode(nodeId, filesCount) {
      for (const i in this.allNodes) {
        if (nodeId === this.allNodes[i].id) {
          this.allNodes[i].filesCount = Math.max(0, (this.allNodes[i].filesCount || 0) - filesCount);
          break;
        }
      }
    },
    createManyNodes: async function(podsData) {
      try {
        const result = await Api.node.createManyNodes(podsData)
        const createdNodes = result.data;

        // this._addOrUpdateNodes(createdNodes);
        return createdNodes;
      } catch (error) {
        console.error('Error during creation of many pods: ', error);
        throw error;
      }
    },
    updateNodeName(node) {
      for (let i = 0; i < this.allNodes.length; i++) {
        if (this.allNodes[i].id === node.id) {
          this.allNodes[i].name = node.name;
          break;
        }
      }
    },
    reset() {
      this.allNodes = [];
      this.currentNodeId = null;
      this.selectedNodeIds = [];
    }
  },
});
